/**
 * canvas-engine/presets/decompositionAdapters/chrome.js
 *
 * Chrome (Header / Footer) family adapter. Header and Footer Chrome_Sections do
 * NOT fit the core engine's vertical text-stack layout — they are horizontal
 * compositions of a logo, navigation, social icons, and small text/links. This
 * adapter therefore builds the Canvas_Model directly from the transcribed
 * chrome DATA, emitting individually editable `logo`, `nav`, `social`, and
 * `text` Canvas_Elements (plus action `button`s for header CTAs) so each is
 * edited through the same Inspector controls as every other section.
 *
 *   Header → editable logo (left) + nav (center) + social/CTA buttons (right),
 *            arranged horizontally across the top bar.
 *   Footer → logo + brand/copyright text + nav link columns + social + legal /
 *            contact text, arranged in columns.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules
 * (`./buildSectionCanvas` helpers + `../../model/*`). Pure, deterministic
 * (stable ids from `sectionType` + ordinal). Never emits a `native` element.
 *
 * Requirements: 4.2, 5.1, 5.7, 17.1, 17.2, 17.3
 */

import { REFERENCE_FRAME_WIDTH, deriveFrameStyle, textElement } from './buildSectionCanvas';
import { normalizeCanvas } from '../../model/canvasModel';
import { normalizeAsset, isEmptyAsset } from '../../model/assetRef';
import { MIN_DIMENSION } from '../../model/schema';
import { CHROME_PRESETS } from '../presetData/chromePresets';

/** The Chrome_Section types this adapter handles. */
export const CHROME_TYPES = Object.freeze(['header', 'footer']);

/** True when `sectionType` is a Chrome_Section (header / footer). */
export function handlesChromeType(sectionType) {
  const t = typeof sectionType === 'string' ? sectionType.trim().toLowerCase() : '';
  return t === 'header' || t === 'footer';
}

// ── Small pure helpers ───────────────────────────────────────────────────────

function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function deepClone(v) {
  return JSON.parse(JSON.stringify(v == null ? null : v));
}

/** Parse a CSS pixel value ('70px', 70) to a number, else the fallback. */
function parsePx(v, fallback) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return lo;
  return Math.min(Math.max(n, lo), hi);
}

/** Map a transcribed link descriptor to a nav/social item. */
function toLinkItem(link) {
  const l = isPlainObject(link) ? link : {};
  const item = {
    label: pickString(l.label),
    url: pickString(l.route) || pickString(l.url),
    visible: l.visible !== false,
  };
  if (pickString(l.icon)) item.icon = pickString(l.icon);
  if (pickString(l.platformKey)) item.platformKey = pickString(l.platformKey);
  return item;
}

// ── Element factories specific to chrome ──────────────────────────────────────

function logoElement(id, frame, zIndex, asset, alt, style = {}) {
  return {
    id,
    type: 'logo',
    ...frame,
    zIndex,
    content: { asset: normalizeAsset(asset), alt: pickString(alt) || 'Logo' },
    style: { background: 'transparent', radius: 0, ...style },
  };
}

function navElement(id, frame, zIndex, items, style = {}) {
  return {
    id,
    type: 'nav',
    ...frame,
    zIndex,
    content: { items: Array.isArray(items) ? items : [] },
    style: { background: 'transparent', ...style },
  };
}

function socialElement(id, frame, zIndex, items, style = {}) {
  return {
    id,
    type: 'social',
    ...frame,
    zIndex,
    content: { items: Array.isArray(items) ? items : [] },
    style: { background: 'transparent', ...style },
  };
}

function buttonElement(id, frame, zIndex, label, url, style = {}) {
  return {
    id,
    type: 'button',
    ...frame,
    zIndex,
    content: { label: pickString(label) || 'Button', url: pickString(url), linkType: 'url', newTab: false },
    style,
  };
}

// ── Header decomposition ──────────────────────────────────────────────────────

function buildHeaderCanvas(data, opts) {
  const content = isPlainObject(data.content) ? data.content : {};
  const style = isPlainObject(data.style) ? data.style : {};
  const sectionType = pickString(opts.sectionType) || pickString(data.sectionType) || 'header';
  const idPrefix = pickString(opts.idPrefix) || sectionType;
  const frameWidth = Number.isFinite(opts.frameWidth) && opts.frameWidth >= MIN_DIMENSION ? opts.frameWidth : REFERENCE_FRAME_WIDTH;

  const headerHeight = clamp(parsePx(style.height, 72), 48, 160);
  const padX = 24;
  const textColor = pickString(style.textColor) || pickString(style.brandColor) || '#1f2937';
  const accent = pickString(style.accentColor) || '#2563eb';

  let ordinal = 0;
  const id = (kind) => {
    const v = `${idPrefix}-${kind}-${ordinal}`;
    ordinal += 1;
    return v;
  };
  let z = 1;
  const pushZ = () => {
    const v = z;
    z += 1;
    return v;
  };

  const elements = [];

  // Logo (left), vertically centered.
  const logo = isPlainObject(content.logo) ? content.logo : {};
  const logoW = clamp(parsePx(logo.width, 120), 48, 240);
  const logoH = clamp(headerHeight - 24, 24, 64);
  const logoY = Math.max((headerHeight - logoH) / 2, 0);
  elements.push(
    logoElement(id('logo'), { x: padX, y: logoY, width: logoW, height: logoH }, pushZ(), logo.asset, logo.alt || content.brandName, { color: textColor }),
  );

  // Right cluster: header CTAs as buttons (sign-in / cart / primary CTA).
  const ctaLabels = [];
  [content.ctaLabel, content.signInLabel, content.cartLabel].forEach((label) => {
    const l = pickString(label);
    if (l && !ctaLabels.includes(l)) ctaLabels.push(l);
  });
  const btnHeight = clamp(headerHeight - 28, 28, 48);
  const btnY = Math.max((headerHeight - btnHeight) / 2, 0);
  const btnWidths = ctaLabels.map((l) => clamp(l.length * 9 + 32, 88, 180));
  const btnGap = 12;
  const rightWidth = btnWidths.reduce((a, b) => a + b, 0) + Math.max(ctaLabels.length - 1, 0) * btnGap;
  const rightStartX = Math.max(frameWidth - padX - rightWidth, padX);

  // Nav (center), filling the space between the logo and the right cluster.
  const navItems = (Array.isArray(content.nav) ? content.nav : []).map(toLinkItem);
  const navX = padX + logoW + 32;
  const navHeight = clamp(headerHeight - 28, 28, 48);
  const navY = Math.max((headerHeight - navHeight) / 2, 0);
  const navWidth = Math.max(rightStartX - navX - 24, 120);
  if (navItems.length > 0) {
    elements.push(navElement(id('nav'), { x: navX, y: navY, width: navWidth, height: navHeight }, pushZ(), navItems, { color: textColor }));
  }

  // Header social icons, when present (rare), placed just left of the CTAs.
  const socialItems = (Array.isArray(content.social) ? content.social : []).map(toLinkItem);
  if (socialItems.length > 0) {
    const socialWidth = clamp(socialItems.length * 32, 64, 240);
    const socialX = Math.max(rightStartX - socialWidth - 16, navX);
    elements.push(socialElement(id('social'), { x: socialX, y: navY, width: socialWidth, height: navHeight }, pushZ(), socialItems, { color: textColor }));
  }

  // CTA buttons, laid left-to-right within the right cluster.
  let offsetX = rightStartX;
  for (let i = 0; i < ctaLabels.length; i += 1) {
    const label = ctaLabels[i];
    const w = btnWidths[i];
    const isPrimary = label === pickString(content.ctaLabel);
    const btnStyle = isPrimary
      ? { background: accent, color: '#ffffff', borderColor: 'transparent', radius: 8 }
      : { background: 'transparent', color: textColor, borderColor: textColor, radius: 8 };
    elements.push(buttonElement(id('button'), { x: offsetX, y: btnY, width: w, height: btnHeight }, pushZ(), label, content.ctaUrl, btnStyle));
    offsetX += w + btnGap;
  }

  const frameStyle = deriveFrameStyle({ ...style, padding: '0' });
  return normalizeCanvas({
    width: frameWidth,
    height: headerHeight,
    ...frameStyle,
    elements,
    source: {
      type: sectionType,
      preserved: { content: deepClone(content), style: deepClone(style), layout: deepClone(data.layout) },
    },
  });
}

// ── Footer decomposition ──────────────────────────────────────────────────────

function buildFooterCanvas(data, opts) {
  const content = isPlainObject(data.content) ? data.content : {};
  const style = isPlainObject(data.style) ? data.style : {};
  const layout = isPlainObject(data.layout) ? data.layout : {};
  const sectionType = pickString(opts.sectionType) || pickString(data.sectionType) || 'footer';
  const idPrefix = pickString(opts.idPrefix) || sectionType;
  const frameWidth = Number.isFinite(opts.frameWidth) && opts.frameWidth >= MIN_DIMENSION ? opts.frameWidth : REFERENCE_FRAME_WIDTH;

  const padX = 32;
  const padTop = 56;
  const headingColor = pickString(style.headingColor) || pickString(style.textColor) || '#1f2937';
  const linkColor = pickString(style.linkColor) || pickString(style.textColor) || '#374151';

  let ordinal = 0;
  const id = (kind) => {
    const v = `${idPrefix}-${kind}-${ordinal}`;
    ordinal += 1;
    return v;
  };
  let z = 1;
  const pushZ = () => {
    const v = z;
    z += 1;
    return v;
  };

  const contentWidth = Math.max(frameWidth - padX * 2, MIN_DIMENSION);
  const elements = [];

  // Brand row: logo (when present) + brand/copyright lead text.
  const logo = isPlainObject(content.logo) ? content.logo : {};
  const brandName = pickString(content.copyrightName) || pickString(content.brandName);
  let y = padTop;
  if (logo.asset && !isEmptyAsset(normalizeAsset(logo.asset))) {
    const logoW = clamp(parsePx(logo.width, 140), 64, 240);
    elements.push(logoElement(id('logo'), { x: padX, y, width: logoW, height: 56 }, pushZ(), logo.asset, logo.alt || brandName, { color: headingColor }));
  } else if (brandName) {
    elements.push(
      textElement(id('text'), { x: padX, y, width: Math.min(360, contentWidth), height: 48 }, pushZ(), { eyebrow: '', heading: brandName, body: '' }, {
        headingColor,
        bodyColor: linkColor,
        eyebrowColor: headingColor,
        textAlign: 'left',
      }),
    );
  }
  y += 96;

  // Link / social / contact column groups.
  const groups = [];
  const addNavGroup = (title, links) => {
    const items = (Array.isArray(links) ? links : []).map(toLinkItem).filter((it) => it.label);
    if (items.length > 0) groups.push({ kind: 'nav', title: pickString(title), items });
  };
  addNavGroup(content.quickLinksTitle, content.quickLinks);
  addNavGroup(content.navigationTitle, content.nav);
  addNavGroup(content.onlineShoppingTitle || content.categoryTitle, content.categories);
  addNavGroup(content.usefulLinksTitle, content.usefulLinks);
  addNavGroup(content.policiesTitle || content.legalTitle, content.legalLinks);

  const socialItems = (Array.isArray(content.social) ? content.social : []).map(toLinkItem).filter((it) => it.label);
  if (socialItems.length > 0) {
    groups.push({ kind: 'social', title: pickString(content.socialTitle) || 'Follow Us', items: socialItems });
  }

  const contact = isPlainObject(content.contact) ? content.contact : null;
  if (contact && (pickString(contact.phone) || pickString(contact.email) || pickString(contact.address))) {
    const lines = [pickString(contact.phone), pickString(contact.email), pickString(contact.address)].filter(Boolean);
    groups.push({ kind: 'contact', title: pickString(content.contactTitle) || 'Contact', body: lines.join('\n') });
  }

  // Lay the groups out in columns across the content width.
  const columns = clamp(layout.columns || groups.length || 1, 1, 4);
  const gap = 24;
  const colWidth = Math.max((contentWidth - (columns - 1) * gap) / columns, MIN_DIMENSION);
  const headingHeight = 28;
  const groupGapY = 12;
  const rowHeight = 200;
  const groupsTop = y;
  let maxRowUsed = 0;

  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i];
    const col = i % columns;
    const row = Math.floor(i / columns);
    maxRowUsed = Math.max(maxRowUsed, row);
    const gx = padX + col * (colWidth + gap);
    const gy = groupsTop + row * rowHeight;

    if (pickString(group.title)) {
      elements.push(
        textElement(id('text'), { x: gx, y: gy, width: colWidth, height: headingHeight }, pushZ(), { eyebrow: '', heading: group.title, body: '' }, {
          headingColor,
          bodyColor: linkColor,
          eyebrowColor: headingColor,
          textAlign: 'left',
        }),
      );
    }
    const bodyY = gy + headingHeight + groupGapY;
    const bodyH = Math.max(rowHeight - headingHeight - groupGapY - 24, MIN_DIMENSION);

    if (group.kind === 'nav') {
      elements.push(navElement(id('nav'), { x: gx, y: bodyY, width: colWidth, height: bodyH }, pushZ(), group.items, { color: linkColor }));
    } else if (group.kind === 'social') {
      const socialH = clamp(group.items.length * 30, 40, bodyH);
      elements.push(socialElement(id('social'), { x: gx, y: bodyY, width: colWidth, height: socialH }, pushZ(), group.items, { color: linkColor }));
    } else if (group.kind === 'contact') {
      elements.push(
        textElement(id('text'), { x: gx, y: bodyY, width: colWidth, height: bodyH }, pushZ(), { eyebrow: '', heading: '', body: group.body }, {
          headingColor,
          bodyColor: linkColor,
          eyebrowColor: headingColor,
          textAlign: 'left',
        }),
      );
    }
  }

  const groupsBottom = groups.length > 0 ? groupsTop + (maxRowUsed + 1) * rowHeight : groupsTop;

  // Legal / copyright line across the bottom.
  const copyright = pickString(content.copyrightText) || (brandName ? `© ${brandName}` : '');
  let bottom = groupsBottom;
  if (copyright) {
    elements.push(
      textElement(id('text'), { x: padX, y: bottom, width: contentWidth, height: 32 }, pushZ(), { eyebrow: '', heading: '', body: copyright }, {
        headingColor,
        bodyColor: linkColor,
        eyebrowColor: headingColor,
        textAlign: 'left',
      }),
    );
    bottom += 32;
  }

  const frameHeight = Math.max(bottom + 48, 160);
  // Honour an explicit minHeight override from the inspector (persisted as
  // `attributes.footer.minHeight` → surfaced on `data.style.minHeight` by
  // buildSectionList in CanvasRuntimePage).
  const minHeightPx = parsePx(style.minHeight, 0);
  const resolvedFrameHeight = minHeightPx > frameHeight ? minHeightPx : frameHeight;
  const frameStyle = deriveFrameStyle({ ...style, padding: '0' });
  return normalizeCanvas({
    width: frameWidth,
    height: resolvedFrameHeight,
    ...frameStyle,
    elements,
    source: {
      type: sectionType,
      preserved: { content: deepClone(content), style: deepClone(style), layout: deepClone(layout) },
    },
  });
}

// ── Public adapter ────────────────────────────────────────────────────────────

/**
 * Resolve a chrome `sectionType_or_data` argument into a concrete descriptor.
 * A descriptor is used as-is; a `header`/`footer` string is looked up in
 * `CHROME_PRESETS[domain]` (default `generic`) so the migration / registry can
 * dispatch by type alone.
 *
 * @param {(string|object)} input `'header'` / `'footer'` or a chrome descriptor
 * @param {object} opts `{ sectionType?, domain? }`
 * @returns {{ data: object, type: string }}
 */
function resolveChrome(input, opts) {
  if (isPlainObject(input)) {
    const type = pickString(opts.sectionType) || pickString(input.sectionType) || 'header';
    return { data: input, type };
  }
  const type = pickString(input) || pickString(opts.sectionType) || 'header';
  const domain = pickString(opts.domain) || 'generic';
  const domainPresets = isPlainObject(CHROME_PRESETS[domain]) ? CHROME_PRESETS[domain] : CHROME_PRESETS.generic;
  const isFooter = type.toLowerCase().includes('footer');
  const data = (isPlainObject(domainPresets) && (isFooter ? domainPresets.footer : domainPresets.header)) || {};
  return { data: isPlainObject(data) ? data : {}, type };
}

/**
 * Decompose a Header or Footer Chrome_Section into a normalized Canvas_Model of
 * individually editable logo / nav / social / text (+ button) Canvas_Elements.
 *
 * Accepts EITHER a transcribed chrome descriptor OR a `'header'` / `'footer'`
 * string (looked up in `CHROME_PRESETS` by `options.domain`). The header/footer
 * mode is chosen from the resolved `sectionType`: anything containing "footer"
 * builds a footer, everything else builds a header.
 *
 * @param {(string|object)} sectionTypeOrData `'header'` / `'footer'` or a
 *   transcribed chrome preset DATA descriptor
 * @param {object} [options] `{ frameWidth?, idPrefix?, sectionType?, domain? }`
 * @returns {object} a normalized Canvas_Model
 */
export function chromeToCanvas(sectionTypeOrData, options = {}) {
  const opts = isPlainObject(options) ? options : {};
  const { data, type } = resolveChrome(sectionTypeOrData, opts);
  const resolvedOpts = {
    frameWidth: REFERENCE_FRAME_WIDTH,
    ...opts,
    sectionType: type,
    idPrefix: pickString(opts.idPrefix) || type,
  };
  if (type.toLowerCase().includes('footer')) {
    return buildFooterCanvas(data, resolvedOpts);
  }
  return buildHeaderCanvas(data, resolvedOpts);
}

export default chromeToCanvas;
